<template lang="pug">
.container
  custom-product-summary(
    summaryClass="slinger-summary"
    :buyRequest="buyRequest"
    :childBuyRequest="mainRack"
    :groupMeta="groupMeta"
    :badgeMeta="badgeMeta"
  )
  Accordion.accordian-styles(
    v-model="openAccordions[0]"
    heading="Rack Configuration"
    open-icon="arrow-up"
    closed-icon="arrow-down"
    :theme="'custom-rhpa-styles'"
    heading-tag="h3"
  )
    bundle-option(
      v-for="child in rackOptionsBeforePullUpBar"
      :key="child.metadata?.bundleOptionId"
      :buyRequest="child"
      :bundleBuyRequest="mainRack"
    )
    configuration(
      v-if="headerChild"
      :buyRequest="headerChild"
      :bundleBuyRequest="mainRack"
      :ignoreAttributes="headerIgnoreForRackConfig"
      :hiddenAttributes="headerHiddenAttributes"
      :grayedOutValues="headerGrayedOutValues"
    )
      slot(name="rhpaBody")
        rhpa-body(:buyRequest="mainRack" :groupMeta="groupMeta")
    bundle-option(
      v-for="child in rackOptionsFromPullUpBar"
      :key="child.metadata?.bundleOptionId"
      :buyRequest="child"
      :bundleBuyRequest="mainRack"
    )
  .side-container
    custom-side-selector(
      :sideOptions="sideOptions"
      :modelValue="sideSelection"
      :show="!!headerChild"
      @update:modelValue="handleSideSelectionChange"
    )
    custom-side-accordion(
      v-if="leftSideEnabled"
      v-model="openAccordions[1]"
      heading="Left Side"
      :buyRequest="leftSide"
      :groupMeta="groupMeta"
    )
      template(#rhpaBody)
        slot(name="rhpaBody")
    custom-side-accordion(
      v-if="rightSideEnabled"
      v-model="openAccordions[2]"
      heading="Right Side"
      :buyRequest="rightSide"
      :groupMeta="groupMeta"
    )
      template(#rhpaBody)
        slot(name="rhpaBody")
  .divider
</template>

<script lang="ts" setup>
import { computed, provide, ref, watchEffect, toRef } from 'vue'
import BundleOption from './bundle-option.vue'
import Configuration from './configuration.vue'
import RhpaBody from './rhpa-body.vue'
import CustomProductSummary from './custom/custom-product-summary.vue'
import CustomSideSelector from './custom/custom-side-selector.vue'
import CustomSideAccordion from './custom/custom-side-accordion.vue'
import { useSideSelection } from './custom/useSideSelection'
import { getBuyRequestMeta, getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { getSlingerXGrayedOutValues } from './bundle-utils/rules/slingerX-rules'
import { useProductStore } from '@/js/stores/productStore'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/RogueTypes'

const props = defineProps<{ buyRequest: BuyRequest }>()
const buyRequestRef = toRef(props, 'buyRequest')

const rogue = useRogue()
const productStore = useProductStore()

const openAccordions = ref<boolean[]>([true, false, false])

// SLINGER-X-GROUP's 3 real bundle children — mirrors Cave; only attachments split per side, weight stack/shrouds stay on mainRack's "Functional Tainer:".
const mainRack = computed(() => props.buyRequest.children?.[0])

const {
  sideOptions,
  leftSide,
  rightSide,
  leftSideEnabled,
  rightSideEnabled,
  sideSelection,
  setSideSelection,
} = useSideSelection<'Dual' | 'Left' | 'Right'>(buyRequestRef, {
  values           : { dual: 'Dual', left: 'Left', right: 'Right' },
  applyOnMount     : true,
  freshLoadDefault : 'Left',
})

function handleSideSelectionChange(val: string) {
  setSideSelection(val as 'Dual' | 'Left' | 'Right')
}

const groupMeta = computed(() => getBuyRequestMeta(props.buyRequest, rogue))
const badgeMeta = computed(() => getBuyRequestMeta(leftSide.value ?? props.buyRequest, rogue))

const rackHeight = computed(() => getSimpleForBuyRequest(props.buyRequest, rogue)?.attributes?.rack_height)

provide('rackHeight', rackHeight)

function getOptionTitle(child: BuyRequest): string {
  const mainRackProduct = productStore.products[mainRack.value?.productId ?? '']

  return mainRackProduct?.bundleOptions?.find(o => o.id === child.metadata?.bundleOptionId)?.title ?? ''
}

const mainRackOptionChildren = computed(() => mainRack.value?.children?.filter((c: BuyRequest) => c.metadata?.bundleOptionId) ?? [])
const headerChild = computed(() => {
  const found = mainRackOptionChildren.value.find(child => getOptionTitle(child).trim().toLowerCase().includes('functional'))

  return found
})

const rackOptions = computed(() => mainRackOptionChildren.value.filter(child => child !== headerChild.value))

// Resistance Type/Shrouds (headerChild) render between Rack Type and Front Pull-Up Bar, matching INDY's option order.
// The add-on bundle has no "Front Pull-Up Bar:" option at all, so a -1 index falls through to "after header" instead of "before".
const pullUpBarIndex = computed(() => rackOptions.value.findIndex(child => getOptionTitle(child).toLowerCase().includes('pull')))
const rackOptionsBeforePullUpBar = computed(() => pullUpBarIndex.value === -1 ? [] : rackOptions.value.slice(0, pullUpBarIndex.value))
const rackOptionsFromPullUpBar = computed(() => pullUpBarIndex.value === -1 ? rackOptions.value : rackOptions.value.slice(pullUpBarIndex.value))

// "Rack:" overlaps Height/Rack Depth/Rack Type with "Functional Tainer:" — replicate bundle-option.vue's attributeOverlapWithMain suppression since headerChild bypasses it.
const rackProduct = computed(() => productStore.products[rackOptions.value[0]?.productId ?? ''])

const headerProduct = computed(() => productStore.products[headerChild.value?.productId ?? ''])
const headerAttributeCodes = computed(() => (headerProduct.value?.configurableAttributes ?? []).map(attr => attr.code))
const headerOverlapWithRack = computed(() => {
  const rackAttrCodes = (rackProduct.value?.configurableAttributes ?? []).map(attr => attr.code)

  return headerAttributeCodes.value.filter(code => rackAttrCodes.includes(code))
})

// "Functional Tainer:" has exactly one Configuration instance (single writer) — side is hidden from its UI and fed in via hiddenAttributes instead of a second live instance.
const headerIgnoreForRackConfig = computed(() => ['side', ...headerOverlapWithRack.value])

// The add-on has no separate "Rack:" bundle option, so bundle-option.vue's grayedOutConfigAttrs never runs for it - mirror it here.
const headerGrayedOutValues = computed(() => {
  const mainSimple = productStore.products[headerChild.value?.children?.[0]?.productId ?? '']
  const mainRackProduct = productStore.products[mainRack.value?.productId ?? '']

  return getSlingerXGrayedOutValues(String(mainSimple?.attributes?.rack_size ?? ''), mainRackProduct?.sku)
})

// Pushed into the single Configuration instance's hiddenAttributes so "Functional Tainer:"'s side always matches which side bundles are toggled on.
const headerHiddenAttributes = computed(() => ({ side: sideSelection.value }))

// Keep mainRack qty at 1
watchEffect(() => {
  if (mainRack.value?.qty === 0) mainRack.value.qty = 1
})
</script>

<style lang="scss" scoped>
.container {
  font-size: $size-xs;
  margin-bottom: 10px;

  .divider {
    height: 1px;
    background-color: $color-gray;
    margin: $space-xxs 0 $size-s;
  }
}

</style>
